import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { motion } from "framer-motion";
import TopBarOfMarketing from "../../components/TopBarOfMarketing";
import Footer from "../../components/Footer";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TenantService from "../../services/tenantService";
import { COUNTRIES } from "../../utils/countries";
import Seo from "../../seo/Seo";
import { useRecaptchaToken, RECAPTCHA_ACTIONS } from "../../utils/recaptcha";

export default function TenantRegister({ darkMode, setDarkMode }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t } = useTranslation('register');
  const { t: tSeo } = useTranslation('seo');
  const tenantService = new TenantService();
  const getRecaptchaToken = useRecaptchaToken();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone_number: "",
    email: "",
    plan_type: "NONE",
    password: "",
    password_confirmation: "",
    nationality: "",
  });
  const [error, setError] = useState("");

  const steps = useMemo(() => [
    t('steps.institutionInfo'),
    t('steps.contactInfo'),
    t('steps.accountInfo'),
  ], [t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
      });
  };

  const validateStep = (step) => {
    setError("");
    
    switch (step) {
      case 0: // Institution Information
        if (!formData.name || !formData.name.trim()) {
          setError(t('validation.institutionNameRequired'));
          return false;
        }
        if (!formData.address || !formData.address.trim()) {
          setError(t('validation.addressRequired'));
          return false;
        }
        return true;
        
      case 1: // Contact Information
        if (!formData.phone_number || !formData.phone_number.trim()) {
          setError(t('validation.phoneRequired'));
          return false;
        }
        if (!formData.email || !formData.email.trim()) {
          setError(t('validation.emailRequired'));
          return false;
        }
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError(t('validation.emailInvalid'));
          return false;
        }
        return true;
        
      case 2: // Account Information
        if (!formData.password || !formData.password.trim()) {
          setError(t('validation.passwordRequired'));
          return false;
        }
        if (formData.password.length < 6) {
          setError(t('validation.passwordMinLength'));
          return false;
        }
        if (!formData.password_confirmation || !formData.password_confirmation.trim()) {
          setError(t('validation.confirmPasswordRequired'));
          return false;
        }
        if (formData.password !== formData.password_confirmation) {
          setError(t('validation.passwordsDoNotMatch'));
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setError("");
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If not on last step, validate and move to next
    if (activeStep !== steps.length - 1) {
      handleNext();
      return;
    }
    
    // Validate current step (last step)
    if (!validateStep(activeStep)) {
      return;
    }
    
    // Validate all previous steps
    for (let i = 0; i < steps.length - 1; i++) {
      if (!validateStep(i)) {
        setError(t('validation.completeAllFields'));
        setActiveStep(i);
        return;
      }
    }
    
    setError("");
    try {
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTIONS.TENANT_REGISTER);
      const response = await tenantService.tenantRegister({
        ...formData,
        recaptchaToken,
        recaptchaAction: RECAPTCHA_ACTIONS.TENANT_REGISTER,
      });
      const { token, requires_checkout, pending_activation } = response.data || {};

      // Kayıt sonrası oturum açılmaz; süper yönetici aktifleştirdikten sonra giriş → checkout akışı login'de.
      localStorage.removeItem("tokenTenant");
      localStorage.removeItem("tenantId");
      localStorage.removeItem("tenantInfo");
      localStorage.removeItem("tenantRequiresCheckout");

      if (token) {
        localStorage.setItem("tokenTenant", token);
      }
      if (response.data?.tenant?.id && token) {
        localStorage.setItem("tenantId", response.data.tenant.id);
        localStorage.setItem("tenantInfo", JSON.stringify(response.data.tenant));
      }
      if (token && requires_checkout) {
        localStorage.setItem("tenantRequiresCheckout", "true");
      }

      setOpenSnackBar(true);
      setTimeout(() => {
        if (token && requires_checkout) {
          navigate("/tenant/checkout");
        } else if (token) {
          navigate("/tenant/dashboard");
        } else {
          navigate("/corporate-login", {
            replace: true,
            state: pending_activation ? { pendingTenantActivation: true } : undefined,
          });
        }
      }, 800);
    } catch (err) {
      setError(err.response?.data?.error || t('validation.registrationError'));
    }
  };

  const renderStepContent = (step) => {
    const commonTextFieldProps = {
      fullWidth: true,
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px",
          "&:hover fieldset": {
            borderColor: colors.greenAccent[500],
          },
        },
        "& .MuiInputBase-input": {
          color: darkMode ? "white" : colors.grey[900],
        },
        "& .MuiInputLabel-root": {
          color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500],
        },
        mb: 3,
      },
    };

    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.institutionName')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('fields.institutionNamePlaceholder')}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.address')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('fields.addressPlaceholder')}
              multiline
              rows={2}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
              }}
            />
          </motion.div>
        );
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.phoneNumber')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="phone_number"
              value={formData.phone_number}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow numbers, spaces, +, -, ( and ) characters
                if (/^[0-9+\-() ]*$/.test(value)) {
                  handleChange(e);
                }
              }}
              placeholder={t('fields.phoneNumberPlaceholder')}
              required
              inputProps={{
                inputMode: "tel",
                pattern: "[0-9+\\-() ]*"
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.email')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('fields.emailPlaceholder')}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.nationality')}
            </Typography>
            <FormControl
              fullWidth
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "&:hover fieldset": {
                    borderColor: colors.greenAccent[500],
                  },
                },
                "& .MuiInputBase-input": {
                  color: darkMode ? "white" : colors.grey[900],
                },
              }}
            >
              <Select
                name="nationality"
                value={formData.nationality || ""}
                onChange={handleChange}
                displayEmpty
                IconComponent={ExpandMoreIcon}
                startAdornment={
                  <InputAdornment position="start">
                    <PublicIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500], ml: 1 }} />
                  </InputAdornment>
                }
                sx={{
                  "& .MuiSelect-select": {
                    paddingLeft: "48px",
                    color: formData.nationality ? (darkMode ? "white" : colors.grey[900]) : (darkMode ? "rgba(255, 255, 255, 0.5)" : colors.grey[400]),
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: darkMode ? "rgba(255, 255, 255, 0.5)" : colors.grey[400] }}></span>;
                  }
                  return selected;
                }}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.password')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              placeholder={t('fields.passwordPlaceholder')}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? (
                        <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      ) : (
                        <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="body2" sx={{ mb: 1, color: darkMode ? "white" : colors.grey[900] }}>
              {t('fields.confirmPassword')}
            </Typography>
            <TextField
              {...commonTextFieldProps}
              name="password_confirmation"
              type={showPasswordConfirm ? "text" : "password"}
              value={formData.password_confirmation}
              onChange={handleChange}
              placeholder={t('fields.confirmPasswordPlaceholder')}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                      {showPasswordConfirm ? (
                        <VisibilityOffIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      ) : (
                        <VisibilityIcon sx={{ color: darkMode ? "rgba(255, 255, 255, 0.7)" : colors.grey[500] }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ 
      width: "100%",
      background: darkMode ? colors.primary[500] : 'white',
      color: darkMode ? 'white' : colors.grey[100],
    }}>
      <Seo
        path="/tenantRegister"
        title={tSeo("register.title")}
        description={tSeo("register.description")}
        keywords={tSeo("register.keywords")}
      />
      <TopBarOfMarketing darkMode={darkMode} setDarkMode={setDarkMode} />
      <Box
        sx={{
          minHeight: "100vh",
          background: darkMode 
            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.greenAccent[700]})`
            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.greenAccent[500]})`,
          display: "flex",
          flexDirection: "column",
          mt: "64px",
        }}
      >
        <Container maxWidth="sm" sx={{ py: 8, flex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={3}
          sx={{
                p: 4,
                borderRadius: "24px",
                background: darkMode ? colors.primary[400] : "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                color: darkMode ? 'white' : colors.grey[100],
              }}
            >
              <Typography
                variant="h3"
                  sx={{
                  textAlign: "center",
                  mb: 4,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.blueAccent[400]})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t('title')}
              </Typography>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel 
                      sx={{
                        "& .MuiStepLabel-label": {
                          color: darkMode ? "white" : colors.grey[900],
                        },
                        "& .MuiStepLabel-label.Mui-active": {
                          color: darkMode ? colors.greenAccent[500] : colors.greenAccent[600],
                          fontWeight: 600,
                        },
                        "& .MuiStepLabel-label.Mui-completed": {
                          color: darkMode ? colors.greenAccent[500] : colors.greenAccent[600],
                        },
                        "& .MuiStepIcon-root": {
                          color: darkMode ? "rgba(255, 255, 255, 0.3)" : colors.grey[300],
                        },
                        "& .MuiStepIcon-root.Mui-active": {
                          color: darkMode ? colors.greenAccent[500] : colors.greenAccent[600],
                        },
                        "& .MuiStepIcon-root.Mui-completed": {
                          color: darkMode ? colors.greenAccent[500] : colors.greenAccent[600],
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <form onSubmit={handleSubmit} noValidate>
                {renderStepContent(activeStep)}

                {error && (
                  <Typography
                    color="error"
                    variant="body2"
                    sx={{ mb: 2, textAlign: "center" }}
                  >
                    {error}
                  </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    sx={{
                      borderColor: darkMode ? colors.greenAccent[500] : colors.grey[800],
                      color: darkMode ? colors.greenAccent[500] : colors.grey[800],
                      "&:hover": {
                        borderColor: darkMode ? colors.greenAccent[600] : colors.grey[900],
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {t('buttons.back')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      background: `linear-gradient(135deg, ${colors.greenAccent[500]}, ${colors.greenAccent[600]})`,
                      color: "white",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${colors.greenAccent[600]}, ${colors.greenAccent[700]})`,
                      },
                    }}
                  >
                    {activeStep === steps.length - 1 ? t('buttons.completeRegistration') : t('buttons.next')}
                  </Button>
                </Box>
              </form>
            </Paper>
          </motion.div>
        </Container>

        <Footer />

                  <Snackbar
                    open={openSnackBar}
                    autoHideDuration={7000}
          onClose={() => setOpenSnackBar(false)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                  >
                    <Alert
            onClose={() => setOpenSnackBar(false)}
                      severity="success"
                      variant="filled"
            sx={{
              width: "100%",
              borderRadius: "12px",
              backgroundColor: colors.greenAccent[500],
            }}
          >
            <Typography variant="body1" color="white">
              {t('success.title')}
                      </Typography>
                    </Alert>
                  </Snackbar>
      </Box>
    </Box>
  );
}
